<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Searchers often look for <strong>Google Maps 360</strong> when they
            want to see inside a shop, clinic, or office before they visit. In
            practice, that usually means <strong>360° photos on Google Maps</strong>
            powered by <strong>Google Street View</strong> — visible in your
            Business Profile, on the map, and in the
            <strong>Street View app</strong>.
          </p>
          <p>
            This guide explains where Maps 360 appears, how to use the Street
            View app for simple captures, and when to build a full tour in
            Travvir and
            <a href="/blog/en/google-street-view"
              >publish directly to Google Street View</a
            >.
            <strong>For now,</strong> Travvir publish uses
            <strong>Travvir’s Google Street View account</strong>, not your
            personal Google login.
          </p>
        </section>

        <section>
          <h2>What Does “Google Maps 360” Mean?</h2>
          <p>
            “Google Maps 360” is not a separate product name from Google — it is
            how users describe <strong>immersive 360° coverage</strong> on a
            business listing. Your connected panoramas show as interactive
            spheres: viewers drag to look around and tap arrows to move between
            scenes inside your location.
          </p>
          <p>
            The same imagery can surface in Google Search when someone looks up
            your business name, in the Maps photo gallery, and when explorers
            browse nearby in the Street View app.
          </p>
        </section>

        <section>
          <h2>Where Your Maps 360 Content Appears</h2>
          <ul>
            <li>
              <strong>Google Search:</strong> Business Profile panel with photos
              and “See inside” style 360 viewing.
            </li>
            <li>
              <strong>Google Maps:</strong> Listing photos, map pins, and linked
              indoor blue dots.
            </li>
            <li>
              <strong>Street View app:</strong> Full-screen panorama viewing and
              neighborhood discovery on iOS and Android.
            </li>
          </ul>
        </section>

        <section>
          <h2>Option 1 — Street View App (Free, Single-Scene Friendly)</h2>
          <p>
            The official <strong>Street View app</strong> from Google is the
            fastest free path when you only need one or two photo spheres and
            you are comfortable capturing on-site with Google’s guided dots.
          </p>
          <h3>Step-by-step</h3>
          <ol>
            <li>
              Download the <strong>Street View app</strong> (iOS or Android) and
              sign in with the Google account tied to your Business Profile.
            </li>
            <li>
              Tap the camera icon and follow the on-screen alignment dots to
              capture a full sphere (usually 12–14 positions).
            </li>
            <li>
              Confirm the map pin at your business address and publish to
              Google Maps.
            </li>
            <li>
              Wait for Google’s review (often 24–72 hours) before the sphere is
              public.
            </li>
          </ol>
          <p>
            For multiple rooms, you repeat capture per scene and link positions
            on the map — workable, but slower than a dedicated tour builder.
          </p>
        </section>

        <section>
          <h2>Option 2 — Travvir (Multi-Room Tours + Direct Publish)</h2>
          <p>
            Use Travvir when you want a polished multi-room tour for your
            website <em>and</em> the same capture on Maps 360. Capture on your
            phone, link rooms, add hotspots on the Travvir tour, then publish the
            360° set to Google from Share → Google Street View. Travvir uploads
            through its <strong>authorized Street View publisher account</strong>
            — you confirm your business address and scenes in the app; no personal
            Google login is required for publish today. Full steps are in our
            <a href="/blog/en/google-street-view"
              >Google Street View publish guide</a
            >.
          </p>
        </section>

        <section>
          <h2>Street View App vs Travvir</h2>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Street View app</th>
                <th>Travvir</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cost</td>
                <td>Free</td>
                <td>Free trial available; paid plans for advanced hosting</td>
              </tr>
              <tr>
                <td>Multi-room tours</td>
                <td>Manual per scene</td>
                <td>Built-in room linking and AI stitch</td>
              </tr>
              <tr>
                <td>Website embed with hotspots</td>
                <td>Limited / Maps only</td>
                <td>Full branded Travvir tour</td>
              </tr>
              <tr>
                <td>Publish to Google Maps 360</td>
                <td>Your Google account in the Street View app</td>
                <td>
                  Share → Google Street View (via Travvir’s publisher account)
                </td>
              </tr>
              <tr>
                <td>Best for</td>
                <td>One quick interior sphere</td>
                <td>Businesses, retail, hospitality, offices</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Who Should Invest in Maps 360?</h2>
          <p>
            Retail stores, restaurants, hotels, gyms, clinics, and real-estate
            offices benefit most — anywhere seeing the space builds trust.
            Customers compare you to competitors on Maps; 360 content helps you
            win the “looks legitimate / fits my needs” decision before they
            drive over.
          </p>
        </section>

        <section>
          <h2>FAQ</h2>
          <h3>How long does Google take to approve 360 photos?</h3>
          <p>
            Most uploads are reviewed within 24–72 hours. Complex tours or policy
            checks can take longer.
          </p>
          <h3>Can I publish a residential home to Maps 360?</h3>
          <p>
            Google restricts some residential interior publishing. Commercial
            venues and public-facing businesses are the primary use case; check
            current Google Street View publishing terms for your region.
          </p>
          <h3>Do I need my own Google account to publish with Travvir?</h3>
          <p>
            <strong>Not for Travvir publish today.</strong> Travvir submits
            panoramas through its authorized Street View publisher account. You
            provide accurate business details and scene selection in Travvir. If
            you use the <strong>Street View app</strong> instead, sign in with a
            Google account that owns or manages the Business Profile for that
            address.
          </p>
          <h3>Is Google Maps 360 the same as a Matterport dollhouse?</h3>
          <p>
            No. Maps 360 shows linked panoramas on Google surfaces. A 3D
            dollhouse lives on platforms like Matterport; you can still publish
            panorama tours to Street View from several tools, but Google’s viewer
            stays panorama-based.
          </p>
        </section>

        <section>
          <h2>Continue Reading</h2>
          <ul>
            <li>
              <a href="/blog/en/google-street-view"
                >Publish to Google Street View from Travvir</a
              >
            </li>
            <li>
              <a href="/blog/en/google-business-virtual-tour"
                >Google Business Virtual Tour</a
              >
            </li>
            <li>
              <a href="/blog/en/best-360-camera-app-iphone"
                >Best 360 Camera Apps for iPhone</a
              >
            </li>
            <li>
              <a href="/blog/en/best-360-camera-app-android"
                >Best 360 Camera Apps for Android</a
              >
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find((p) => p.slug === "google-maps-360");

useBlogSeo({
  title:
    "Google Maps 360 & Street View App: Business Guide [2026] | Travvir",
  description:
    "What Google Maps 360 means for your business listing, Street View app vs Travvir, and how Travvir publish works today via Travvir's Google Street View publisher account.",
  keywords:
    "google maps 360, street view app, google street view, 360 google maps business, google business 360 photos",
  slug: "google-maps-360",
  image: postData?.image,
  postData,
  faqItems: [
    {
      question: "What is Google Maps 360 for businesses?",
      answer:
        "It refers to interactive 360° photos on your Google Maps and Business Profile listing, powered by Google Street View technology — not a separate Google product.",
    },
    {
      question: "Should I use the Street View app or Travvir?",
      answer:
        "Use the Street View app for a fast free single sphere with your Google account. Use Travvir for multi-room tours, website embeds with hotspots, and publish via Travvir's Street View publisher account.",
    },
  ],
});
</script>
